import type { FC, ReactNode } from "react";
import { EmptyState as UUIEmptyState } from "../components/application/empty-state/empty-state";
import { Button } from "../components/base/buttons/button";
import { cx } from "../utils/cx";

export type EmptyStateAction = {
    /** May carry a <Latin> run (order ids keep Latin digits). */
    label: ReactNode;
    href?: string;
    onPress?: () => void;
    icon?: FC<{ className?: string }>;
};

export interface PageEmptyStateProps {
    icon?: FC<{ className?: string }>;
    /** Optional illustration (e.g. a generated image) instead of the featured icon. */
    image?: ReactNode;
    /** URL of a transparent illustration (kit `assets/illustrations/empty-*.webp`); replaces the featured icon. */
    illustration?: string;
    /** May carry a <Latin> run (order ids keep Latin digits). */
    title: ReactNode;
    /** What will appear here and what to do to fill it (spec §14.6.8). */
    description: ReactNode;
    /** Short bullet points: what this page will show. */
    points?: string[];
    action?: EmptyStateAction;
    secondaryAction?: EmptyStateAction;
    size?: "sm" | "md" | "lg";
    className?: string;
}

const ActionButton = ({ action, color }: { action: EmptyStateAction; color: "primary" | "secondary" }) =>
    action.href ? (
        <Button href={action.href} color={color} size="md" iconLeading={action.icon}>
            {action.label}
        </Button>
    ) : (
        <Button onPress={action.onPress} color={color} size="md" iconLeading={action.icon}>
            {action.label}
        </Button>
    );

/** Untitled UI EmptyState with guidance copy and a next action: no blank page goes unexplained. */
export const EmptyState = ({ icon, image, illustration, title, description, points, action, secondaryAction, size = "md", className }: PageEmptyStateProps) => (
    <UUIEmptyState size={size} className={cx("mx-auto max-w-xl", className)}>
        <UUIEmptyState.Header pattern={image || illustration ? "none" : "circle"}>
            {image ??
                (illustration ? (
                    <img src={illustration} alt="" aria-hidden="true" className={cx("mx-auto w-auto select-none", size === "sm" ? "h-28" : "h-36 md:h-40")} draggable={false} />
                ) : (
                    <UUIEmptyState.FeaturedIcon icon={icon} color="brand" theme="light" />
                ))}
        </UUIEmptyState.Header>

        <UUIEmptyState.Content>
            <UUIEmptyState.Title>{title}</UUIEmptyState.Title>
            <UUIEmptyState.Description>{description}</UUIEmptyState.Description>
            {points && points.length > 0 && (
                <ul className="mt-3 flex flex-col gap-1.5 text-start text-sm text-tertiary">
                    {points.map((p) => (
                        <li key={p} className="flex gap-2">
                            <span className="mt-2 size-1.5 shrink-0 rounded-full bg-fg-brand-secondary" aria-hidden="true" />
                            <span>{p}</span>
                        </li>
                    ))}
                </ul>
            )}
        </UUIEmptyState.Content>

        {(action || secondaryAction) && (
            <UUIEmptyState.Footer>
                {secondaryAction && <ActionButton action={secondaryAction} color="secondary" />}
                {action && <ActionButton action={action} color="primary" />}
            </UUIEmptyState.Footer>
        )}
    </UUIEmptyState>
);
